import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { subscribeAppResume } from "../services/appResumeService";
import {
  createFamilyInvite,
  getActiveFamilyInvite,
  revokeActiveFamilyInvite,
  remainingInviteSeconds,
} from "../services/familyInviteService";

const INVITE_EVENT = "fp:familyInviteChanged";
const INFO_SEEN_KEY = "fp:familyInviteInfoSeen";
const infoSeenKey = (familyId) => `${INFO_SEEN_KEY}:${String(familyId || "global")}`;
// Davet kodu backend'de hash olarak tutulur ve sonradan geri okunamaz. Açık kodu
// localStorage'a yazmadan, aynı uygulama oturumundaki panel örnekleri arasında
// güvenli biçimde paylaşmak için yalnız bellekte saklarız.
const visibleInviteByFamily = new Map();

function cachedVisibleInvite(familyId) {
  const key = String(familyId || "");
  const invite = visibleInviteByFamily.get(key) || null;
  if (!invite?.code || remainingInviteSeconds(invite.expiresAt, Date.now()) <= 0) {
    visibleInviteByFamily.delete(key);
    return null;
  }
  return invite;
}

function dispatchInviteChanged(familyId, activeInvite = null) {
  try {
    window.dispatchEvent(new CustomEvent(INVITE_EVENT, {
      detail: { familyId: String(familyId || ""), activeInvite },
    }));
  } catch {}
}

export function useFamilyInvite({ familyId, enabled = true } = {}) {
  const [activeInvite, setActiveInvite] = useState(null);
  const [visibleInvite, setVisibleInvite] = useState(() => cachedVisibleInvite(familyId));
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState("");
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());
  const [showEducation, setShowEducation] = useState(false);
  const mountedRef = useRef(true);
  const requestSeqRef = useRef(0);
  const visibleInviteRef = useRef(null);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!enabled || !familyId) {
      setActiveInvite(null);
      setVisibleInvite(null);
      return null;
    }
    const seq = ++requestSeqRef.current;
    if (!silent) setLoading(true);
    setError("");
    try {
      const result = await getActiveFamilyInvite({ familyId });
      if (!mountedRef.current || seq !== requestSeqRef.current) return null;
      const nextActive = result.hasActiveInvite ? result.activeInvite : null;
      setActiveInvite(nextActive);
      const currentVisible = visibleInviteRef.current;
      if (!nextActive || (currentVisible?.id && String(currentVisible.id) !== String(nextActive.id))) {
        visibleInviteRef.current = null;
        setVisibleInvite(null);
      }
      return result;
    } catch (err) {
      if (mountedRef.current && seq === requestSeqRef.current) {
        setError(err?.message || "");
      }
      throw err;
    } finally {
      if (mountedRef.current && seq === requestSeqRef.current && !silent) setLoading(false);
    }
  }, [enabled, familyId]);

  useEffect(() => {
    mountedRef.current = true;
    const cachedInvite = cachedVisibleInvite(familyId);
    visibleInviteRef.current = cachedInvite;
    setVisibleInvite(cachedInvite);
    setActiveInvite(null);
    setError("");
    if (enabled && familyId) refresh().catch(() => {});
    return () => {
      mountedRef.current = false;
      requestSeqRef.current += 1;
    };
  }, [enabled, familyId, refresh]);

  useEffect(() => {
    if (!enabled || !familyId) return undefined;
    const unsubscribeResume = subscribeAppResume(() => refresh({ silent: true }).catch(() => {}), { delayMs: 250 });
    const onChanged = (event) => {
      if (String(event?.detail?.familyId || "") !== String(familyId)) return;
      const sharedInvite = event?.detail?.activeInvite;
      if (sharedInvite?.code && remainingInviteSeconds(sharedInvite.expiresAt, Date.now()) > 0) {
        visibleInviteByFamily.set(String(familyId), sharedInvite);
        visibleInviteRef.current = sharedInvite;
        setVisibleInvite(sharedInvite);
        setActiveInvite(sharedInvite);
        return;
      }
      if (sharedInvite === null) {
        visibleInviteByFamily.delete(String(familyId));
        visibleInviteRef.current = null;
        setVisibleInvite(null);
      }
      refresh({ silent: true }).catch(() => {});
    };
    window.addEventListener(INVITE_EVENT, onChanged);
    return () => {
      unsubscribeResume?.();
      window.removeEventListener(INVITE_EVENT, onChanged);
    };
  }, [enabled, familyId, refresh]);

  useEffect(() => {
    if (!activeInvite && !visibleInvite) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [activeInvite, visibleInvite]);

  const remainingSeconds = useMemo(() => {
    const expiresAt = visibleInvite?.expiresAt || activeInvite?.expiresAt;
    return remainingInviteSeconds(expiresAt, now);
  }, [visibleInvite?.expiresAt, activeInvite?.expiresAt, now]);

  useEffect(() => {
    if (remainingSeconds > 0) return;
    if (visibleInvite) {
      visibleInviteByFamily.delete(String(familyId));
      visibleInviteRef.current = null;
      setVisibleInvite(null);
    }
    if (activeInvite) setActiveInvite(null);
  }, [remainingSeconds, visibleInvite, activeInvite, familyId]);

  const createInvite = useCallback(async (source = "app") => {
    if (!familyId || action) return null;
    setAction("create");
    setError("");
    try {
      const invite = await createFamilyInvite({ familyId, source });
      if (!mountedRef.current) return invite;
      visibleInviteByFamily.set(String(familyId), invite);
      visibleInviteRef.current = invite;
      setVisibleInvite(invite);
      setActiveInvite(invite ? {
        id: invite.id,
        createdAt: new Date().toISOString(),
        expiresAt: invite.expiresAt,
        remainingSeconds: invite.expiresInSeconds,
        codeRecoverable: true,
      } : null);
      try {
        const key = infoSeenKey(familyId);
        const seen = localStorage.getItem(key) === "true";
        setShowEducation(!seen);
        if (!seen) localStorage.setItem(key, "true");
      } catch {
        setShowEducation(true);
      }
      dispatchInviteChanged(familyId, invite);
      return invite;
    } catch (err) {
      if (mountedRef.current) setError(err?.message || "");
      throw err;
    } finally {
      if (mountedRef.current) setAction("");
    }
  }, [familyId, action]);

  const revokeInvite = useCallback(async () => {
    if (!familyId || action) return false;
    setAction("revoke");
    setError("");
    try {
      await revokeActiveFamilyInvite({ familyId });
      if (mountedRef.current) {
        visibleInviteByFamily.delete(String(familyId));
        visibleInviteRef.current = null;
        setVisibleInvite(null);
        setActiveInvite(null);
      }
      dispatchInviteChanged(familyId, null);
      return true;
    } catch (err) {
      if (mountedRef.current) setError(err?.message || "");
      throw err;
    } finally {
      if (mountedRef.current) setAction("");
    }
  }, [familyId, action]);

  const dismissEducation = useCallback(() => {
    setShowEducation(false);
    try { localStorage.setItem(infoSeenKey(familyId), "true"); } catch {}
  }, [familyId]);

  return {
    activeInvite,
    visibleInvite,
    hasActiveInvite: !!activeInvite && remainingSeconds > 0,
    remainingSeconds,
    loading,
    action,
    error,
    refresh,
    createInvite,
    revokeInvite,
    showEducation,
    dismissEducation,
  };
}
